import * as signalR from '@microsoft/signalr';
import { useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { LoggedInAppArgs } from './App';
import { BroadcastCharacterUpdate, BroadcastDiceRollResults, CompleteOpenFireArgs, FalloutActionType, FalloutPrompt, GameData, GamePhase, GameResponse, MapAssetMoveable, OpenFireRequest, PlaceMiniRequest, PromptPlaceMiniResponse, PromptRollDiceResponse, PromptTakeActionResponse, RequestMiniPlaced, RollDicePrompt, SetFullGameRequest, SetGamePhaseResponse, SetPromptResponse, UserStatusUpdate, Vector3, PromptTakeAction, FalloutConfusionZonesPromptRequest, RequestOnActionSelected, RequestOnNewTurn, RequestOnNewRound, RequestGetMoving, ResuestPromptMoveCharacter, RequestMoveCharacter, OnActionCompletedResponse, BrawlRequest, RequestBackOff, RequestBackOffMoveCharacter, RequestPatchUp, Quaternion, RequestDevForceSyncPositions, RequestOnScoreUpdate, RequestOnNarrativeEvents, NarrativeEvent, OnPromptTimeoutWarningRequest, OnAttackResolvedRequest, OnCombatCompletedRequest, OnCombatStartedRequest, OnConfusionTestResultRequest, OnConfusionTestsResolvedRequest, OnConfusionTestsTriggeredRequest, OnCriticalEffectResultRequest, OnCriticalEffectsResolvedRequest, OnCriticalEffectsTriggeredRequest, OnFortuneSmilesTriggeredRequest, OnPromptCompletedRequest, RotateCharacterRequest, OnGameCompletedRequest } from './Data/Response';
import LoadedGameView from './Game/LoadedGame';
import ControlZoneModal from './Game/ControlZoneModal';
import PlaceMiniView from './Game/PlaceMiniView';
import DiceRollResultsView from './Game/DiceRollResultView';
import MoveCharacterModalView from './Game/MoveCharacterModalView';

interface SignalRViewArgs {
    accessToken: string;
}
const GameView: React.FC = () => {
    const args = useOutletContext() as LoggedInAppArgs;
    const params = useParams();
    const gameId = params['gameId'];
    const [runningConnection, setRunningConnection] = useState<signalR.HubConnection | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [phase, setPhase] = useState<GamePhase>("initializing");
    // Clear prompt on every completion (like mini placed, dice roll complete, etc.)
    const [gamePrompt, setGamePrompt] = useState<FalloutPrompt | null>(null);
    const [actionPrompt, setActionPrompt] = useState<PromptTakeAction | null>(null);
    const [diceRolls, setDiceRolls] = useState<BroadcastDiceRollResults[]>([]);
    const [round, setRound] = useState<number | null>(null);
    const [game, setGame] = useState<GameData | null>(null);
    const [hasJoined, setHasJoined] = useState(false);
    const [narrativeEvents, setNarrativeEvents] = useState<NarrativeEvent[]>([]);

    const gameRef = useRef<GameData | null>(null);

    const submitMoveCharacter = async (request: RequestMoveCharacter) => {
        args.setModalContent(null);
        try {
            const temp = await runningConnection.invoke('MoveCharacter', request);
        } catch (e) {
            const err = e as Error;
            console.log(e);
        }
        return;
    }

    const submitBackOffMoveCharacter = async (request: RequestBackOffMoveCharacter) => {
        args.setModalContent(null);
        try {
            const temp = await runningConnection.invoke('BackOffMoveCharacter', request);
        } catch (e) {
            const err = e as Error;
            console.log(e);
        }
        return;
    }

    useEffect(() => {
        gameRef.current = game;

        if (!runningConnection) {
            return;
        }
        if (hasJoined) {
            return;
        }
        console.log("Pre Notify Joined Game.");
        const temp = runningConnection?.invoke('NotifyJoined')
            .then(r => {
                console.log("Post joined the game");
                setHasJoined(true);
            })
            .catch(e => {
                console.log("error notifying");
                console.log(e);
            });
    }, [game]);

    const onDiceRollResultsHandler = (result: BroadcastDiceRollResults) => {
        console.log("Dice results!!");
        console.log(result);
        setDiceRolls(prev => {
            return [
                ...prev,
                result
            ];
        })
        const currentGame = gameRef.current;
        if (!currentGame) {
            return;
        }
        var player = currentGame.falloutPlayers.find(p => p.userId === result.playerUserId);
        if (!player) {
            return;
        }
        args.setModalContent({ element: <DiceRollResultsView results={result} player={player} />, canClose: true })
    };

    const closeConnection = async () => {
        if (!runningConnection) {
            return;
        }
        setIsLoading(true);
        try {
            await runningConnection.stop();
        } catch (e) {
            console.log("Error closing connection");
            console.log(e);
        }
        setRunningConnection(null);
        setIsLoading(false);
    }

    const endTurnHandler = async () => {
        if (!runningConnection) {
            return;
        }
        try {
            const response = await runningConnection.invoke("EndTurn", {});
        } catch (e) {
            var err = e as Error;
            console.log(`Error ending turn: ${err.message}`);
        }
    }
    const passHandler = async () => {
        if (!runningConnection) {
            return;
        }
        try {
            const response = await runningConnection.invoke("Pass", {});
        } catch (e) {
            var err = e as Error;
            console.log(`Error passing: ${err.message}`);
        }
    }
    const resetHandler = async () => {
        if (!runningConnection) {
            return;
        }
        try {
            const response = await runningConnection.invoke("Reset");
        } catch (e) {
            var err = e as Error;
            console.log(`Error passing reset: ${err.message}`);
        }
    }
    const onUserStatusUpdated = (result: UserStatusUpdate) => {
        console.log('user status updated');
        console.log(result);
        setGame(prev => {
            prev.falloutPlayers = prev.falloutPlayers.map(p => {
                if (p.userId !== result.userId) {
                    return p;
                }
                return {
                    ...p,
                    isOnline: result.isOnline
                }
            });
            return { ...prev };
        })
    };


    useEffect(() => {
        if (actionPrompt) {
            console.log("Checking action prompt?");
            if (
                actionPrompt.promptedPlayerUserId !== gamePrompt.promptedPlayerUserId ||
                actionPrompt.type !== gamePrompt.type ||
                actionPrompt.promptedPlayerUserId !== args.loggedInUser.userId
            ) {
                console.log("RESETTING: Action Prompt");
                setActionPrompt(null);
            }
        }
    }, [gamePrompt])

    useEffect(() => {
        setIsLoading(game === null);
        if (!game || !runningConnection) {
            return;
        }
    }, [game]);

    useEffect(() => {
        if (!runningConnection) {
            setGame(null);
            return;
        }
        if (game) {
            return;
        }
        runningConnection.onreconnecting(error => {
            console.log("Connection lost due to error:", error);
        });

        runningConnection.onreconnected(connectionId => {
            console.log("Reconnected with new connection ID:", connectionId);
            runningConnection.invoke("Rejoin", { GameId: gameId });
        });

        runningConnection.on("OnAnotherUserPrompted", (result: SetPromptResponse) => {
            console.log("Another Player Prompted!");
        });
        runningConnection.on("OnGameCompleted", (result: OnGameCompletedRequest) => {
            console.log("OnGameCompleted");
            console.log(result);
        });
        runningConnection.on("OnCombatStarted", (result: OnCombatStartedRequest) => {
            console.log("OnCombatStarted");
            console.log(result);
        });
        runningConnection.on("OnCombatCompleted", (result: OnCombatCompletedRequest) => {
            console.log("OnCombatCompleted");
        });
        runningConnection.on("OnPromptCompleted", (result: OnPromptCompletedRequest) => {
            console.log("OnPromptCompleted");
            console.log(result);
        });
        runningConnection.on("OnAttackResolved", (result: OnAttackResolvedRequest) => {
            console.log("OnAttackResolved");
            console.log(result);
        });
        runningConnection.on("OnCriticalEffectsTriggered", (result: OnCriticalEffectsTriggeredRequest) => {
            console.log("OnCriticalEffectsTriggered");
            console.log(result);
        });
        runningConnection.on("OnCriticalEffectResult", (result: OnCriticalEffectResultRequest) => {
            console.log("OnCriticalEffectResult");
            console.log(result);
        });
        runningConnection.on("OnCriticalEffectsResolved", (result: OnCriticalEffectsResolvedRequest) => {
            console.log("OnCriticalEffectsResolved");
        });
        runningConnection.on("OnConfusionTestsTriggered", (result: OnConfusionTestsTriggeredRequest) => {
            console.log("OnConfusionTestsTriggered");
            console.log(result);
        });
        runningConnection.on("OnConfusionTestResult", (result: OnConfusionTestResultRequest) => {
            console.log("OnConfusionTestResult");
            console.log(result);
        });
        runningConnection.on("OnConfusionTestsResolved", (result: OnConfusionTestsResolvedRequest) => {
            console.log("OnConfusionTestsResolved");
        });
        runningConnection.on("OnFortuneSmilesTriggered", (result: OnFortuneSmilesTriggeredRequest) => {
            console.log("OnFortuneSmilesTriggered");
            console.log(result);
        });
        runningConnection.on("OnGetMovingMoveCharacterComplete", (result: SetPromptResponse) => {
            console.log("Move Character Completed for Get Moving Action");
        });
        runningConnection.on("OnBackOffMoveCharacterComplete", (result: SetPromptResponse) => {
            console.log("Move Character Completed for Back Off Action");
        });
        runningConnection.on("OnPromptTimeoutWarning", (result: OnPromptTimeoutWarningRequest) => {
            console.log("WARNING: Time is getting short");
            console.log(result);
        });
        runningConnection.on("OnPlayerPrompted", (result: SetPromptResponse) => {
            console.log("Player Prompted!");
            console.log(result.prompt);
            setGamePrompt(result.prompt);
        });
        runningConnection.on("OnActionCompleted", (result: OnActionCompletedResponse) => {
            console.log("Action Completed");
            args.setModalContent({
                canClose: true,
                element: <div><h3>Action completed!</h3></div>
            })
        });
        runningConnection.on("OnNarrativeEvents", (result: RequestOnNarrativeEvents) => {
            console.log("narrative events");
            console.log(result);
            setNarrativeEvents(prev => [...prev, ...result.events]);
        });
        runningConnection.on("GetConfusionZones", (result: FalloutConfusionZonesPromptRequest) => {
            console.log("PROMPT: Generate control zones!");
            console.log(result.prompt);
            const currentGame = gameRef.current;
            if (!currentGame) {
                return;
            }
            const p = currentGame.falloutPlayers.find(p => p.userId === args.loggedInUser.userId && p.userId === result.prompt.promptedPlayerUserId);
            if (!p) {
                console.log("FAILED TO FIND PLAYER");
                return;
            }
            args.setModalContent({
                element: <ControlZoneModal
                    crewCharacters={p.crew?.characters ?? []}
                    prompt={result.prompt}
                    returnControlZones={async zones => {
                        args.setModalContent(null);
                        try {
                            const temp = await runningConnection.invoke('ApplyConfusionZones', { ConfusionZones: zones });
                            console.log(temp);
                        } catch (e) {
                            console.log("error with control zone");
                            console.log(e);
                        }
                        setIsLoading(false);
                    }}
                />,
                canClose: false
            })
            setGamePrompt(result.prompt);
        });
        runningConnection.on("promptmovecharacter", (result: ResuestPromptMoveCharacter) => {
            console.log("PROMPT Move Character!");
            console.log(result.prompt);
            const allCharacters = gameRef.current.falloutPlayers.flatMap(p => p.crew?.characters ?? []);
            const c = allCharacters.find(c => c.id === result.prompt.characterId);
            if (!c) {
                return;
            }
            const enemyCharacters = allCharacters.filter(ec => ec.ownerUserId !== c.ownerUserId);
            args.setModalContent({
                element: <MoveCharacterModalView
                    character={c}
                    maxStandardMovement={result.prompt.standardMaxMovement}
                    maxHurryingMovement={result.prompt.hurryMaxMovement}
                    submitMoveCharacter={submitMoveCharacter}
                    enemyCharacters={enemyCharacters}
                />,
                canClose: false
            })
            setGamePrompt(result.prompt);
        });

        runningConnection.on("promptbackoffmovecharacter", (result: ResuestPromptMoveCharacter) => {
            console.log("PROMPT Back Off Move Character!");
            console.log(result.prompt);
            const allCharacters = gameRef.current.falloutPlayers.flatMap(p => p.crew?.characters ?? []);
            const c = allCharacters.find(c => c.id === result.prompt.characterId);
            if (!c) {
                return;
            }
            args.setModalContent({
                element: <MoveCharacterModalView
                    character={c}
                    maxStandardMovement={result.prompt.standardMaxMovement}
                    maxHurryingMovement={result.prompt.hurryMaxMovement}
                    submitMoveCharacter={req => {
                        submitBackOffMoveCharacter({
                            CharacterId: req.CharacterId,
                            DistanceInInches: req.DistanceInInches,
                            Path: req.Path,
                            Rotation: { X: 1, Y: 1, Z: 1, W: 1 }
                        })
                    }}
                />,
                canClose: false
            })
            setGamePrompt(result.prompt);
        });
        runningConnection.on("PromptRollDice", (result: PromptRollDiceResponse) => {
            console.log("PROMPT ROLL DICE!");
            console.log(result.prompt);
            args.setModalContent({
                element: < div >
                    <h2>Roll Some Dice!</h2>
                    <p>{result.prompt.reason}</p>
                    <p>Standard: {result.prompt.standardDice}</p>
                    <p>Luck: {result.prompt.luckDice}</p>
                    <button
                        onClick={async e => {
                            const serverArgs: CompleteOpenFireArgs = {
                                GameId: gameId,
                                StandardDice: new Array(result.prompt.standardDice).fill(0).map(d => Math.floor(Math.random() * 10) + 1),
                                LuckDice: new Array(result.prompt.luckDice).fill(0).map(d => Math.floor(Math.random() * 10) + 1),
                            }
                            console.log(serverArgs);
                            args.setModalContent(null);
                            const temp = await runningConnection.invoke('CompleteDiceRoll', serverArgs);
                            console.log(temp);
                        }}
                    >ROLL!</button>
                </div>,
                canClose: false
            })
            setGamePrompt(result.prompt);
        });
        runningConnection.on("PromptPlaceMini", (result: PromptPlaceMiniResponse) => {
            setActionPrompt(null);
            const currentGame = gameRef.current;
            if (!currentGame) {
                return;
            }
            const me = currentGame.falloutPlayers.find(p => {
                console.log(p.userId);
                console.log(args.loggedInUser.userId);
                console.log()
                return p.userId === args.loggedInUser.userId
            });
            if (!me) {
                throw new Error("I am not a player!");
            }
            const unplacedMinis = me.crew.characters.filter(c => !c.moveableId && c.id === result.prompt.characterId);
            args.setModalContent({
                element: <PlaceMiniView
                    unplacedCharacters={unplacedMinis}
                    setPosition={async (position: Vector3, characterId: string) => {
                        const moveable: MapAssetMoveable = {
                            assetId: 'some-asset-id',
                            moveableId: 'some-moveable-id',
                            isRevealed: true,
                            localPosition: { X: position.X, Y: position.Y, Z: position.Z },
                            localRotation: { X: 0, Y: 0, Z: 0, W: 0 },
                            localScale: { X: 0, Y: 0, Z: 0 },
                            ownerId: me.userId,
                            groupId: null,
                            heroForgeMiniName: null,
                            heroForgeMiniOwnerId: null,
                            isHeroForgeMini: null
                        }
                        const request: PlaceMiniRequest = {
                            GameId: currentGame.id,
                            CharacterId: characterId,
                            Moveable: moveable
                        }
                        const temp = runningConnection?.invoke('PlaceMini', request)
                            .then(r => {
                            })
                            .catch(e => {
                                console.log(e);
                            });
                        args.setModalContent(null);
                    }} />,
                canClose: false
            })
        });
        runningConnection.on("PromptTakeAction", (result: PromptTakeActionResponse) => {
            console.log("YOUR ACTION!!");
            console.log(result.prompt);
            setActionPrompt(result.prompt);
            // args.setModalContent(<div>TAKE AN ACTION!!</div>);
        });
        runningConnection.on("OnScoreUpdate", (result: RequestOnScoreUpdate) => {
            console.log("New score update");
            console.log(result);
        });
        runningConnection.on("OnNewTurn", (result: RequestOnNewTurn) => {
            console.log("New turn");
            console.log(result);
        });
        runningConnection.on("OnNewRound", (result: RequestOnNewRound) => {
            console.log(`New round: ${result.number}`);
            setRound(result.number);
        });
        runningConnection.on("OnDiceRollResults", (result: BroadcastDiceRollResults) => {
            onDiceRollResultsHandler(result);
        });
        runningConnection.on("OnActionSelected", (result: RequestOnActionSelected) => {
            console.log("Action selected!!");
            console.log(result);
            const currentGame = gameRef.current;
            if (!currentGame) {
                return;
            }
            var characters = currentGame.falloutPlayers.flatMap(p => p.crew?.characters ?? []);
            var actingCharacter = characters.find(c => c.id === result.activeCharacterId);
            if (!actingCharacter) {
                return;
            }
            if (actingCharacter.ownerUserId === args.loggedInUser.userId) {
                return;
            }
            var targets = (result.targetCharacterIds) ? characters.filter(c => result.targetCharacterIds.includes(c.id)) : null;
            args.setModalContent({
                element: <div>
                    <img src={actingCharacter.avatar.smallUrl} />
                    <h1>{actingCharacter.name} takes action: {getActionTypeString(result.type)}</h1>
                    {targets && <div>
                        <h3>Targeting:</h3>
                        <ul>
                            {targets.map(t => <li>
                                <img src={t.avatar.smallUrl} />
                                <p>{t.name}</p>
                            </li>)}
                        </ul>
                    </div>}
                </div>,
                canClose: true
            })
        });
        runningConnection.on("OnPlayerStatusUpdate", onUserStatusUpdated);
        runningConnection.on("OnCharacterUpdate", (result: BroadcastCharacterUpdate) => {
            console.log("characters updated!!");
            console.log(result);
            setGame(prev => {
                prev.falloutPlayers.map(p => {
                    if (!p.crew) {
                        return p;
                    }
                    p.crew.characters = p.crew.characters.map(c => {
                        const update = result.activeCharacters.find(u => u.characterId === c.id);
                        if (!update) {
                            return c;
                        }
                        c.state.fatigue = update.fatigue;
                        c.state.harm = update.harm;
                        c.state.injuries = update.injuries;
                        c.state.status = update.status;
                        c.engagedEnemyIds = update.engagedEnemyIds
                        return c;
                    });
                    return p;
                })
                return prev;
            });
            args.setModalContent({
                element: < div >
                    <h2>Results</h2>
                    <p>{result.activeCharacters.length} player(s) have been updated.</p>
                </div>,
                canClose: true
            });

            // setPrompt(result.prompt);
        });

        runningConnection.on("OnMiniPlaced", (result: RequestMiniPlaced) => {
            console.log("Mini Placed!");
            console.log(result);
            setGame(prev => {
                const mapIndex = prev.maps.findIndex(m => m.id === prev.currentMapId);
                prev.maps[mapIndex].mapData.assets.push(result.moveable);
                prev.falloutPlayers = prev.falloutPlayers.map(p => {
                    if (p.userId !== args.loggedInUser.userId) {
                        return p;
                    }
                    if (!p.crew) {
                        return p;
                    }
                    p.crew.characters = p.crew.characters.map(pc2 => {
                        if (pc2.id !== result.characterId) {
                            return pc2;
                        }
                        pc2.moveableId = result.moveable.moveableId;
                        return pc2;
                    });
                    return p;
                });
                return prev;
            })
        });
        runningConnection.on("SetGamePhase", (result: SetGamePhaseResponse) => {
            setPhase(result.phase);
            console.log("📡 PhaseChanged received:", result.phase);
            // if (isMounted) setPhase(newPhase);
        });
        runningConnection.invoke('Join', { GameId: gameId })
            .then(res => {
                const msResponse: GameResponse = res;
                console.log("Joined the Game");
                console.log(msResponse);
                if (!msResponse.isSuccess) {
                    throw new Error(msResponse.message ?? "Unknown error.");
                }
                setGame(msResponse.data);
                setRound(msResponse.data.round ?? null);
            })
            .catch(err => {
                console.error("❌ Failed to connect to game Error: ", err);
                setError(err);
            });
    }, [runningConnection]);

    useEffect(() => {
        let connection: signalR.HubConnection;
        connection = new signalR.HubConnectionBuilder()
            .withUrl("/rpc/fallout-game-controller", {
                // THIS SHOULD REFERENCE ACCESS TOKEN FACTORY INSTEAD OF SINGLE STRING
                accessTokenFactory: () => args.loggedInUser.getAccessToken(),
                transport: signalR.HttpTransportType.WebSockets
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();
        connection.start()
            // })
            .then(() => {
                console.log("✅ Connected to SignalR hub");
                setRunningConnection(connection);
            })
            .catch(err => {
                console.error("❌ SignalR Connection Error: ", err);
                setError(err);
            });

        // return () => {
        //     connection.stop();
        // };
    }, []);

    const startOpenFire = async (openFireArgs: OpenFireRequest) => {
        console.log("START OPEN FIRE");
        // setCanSelectAction(false);
        args.setModalContent(null);
        const temp = await runningConnection.invoke('OpenFire', openFireArgs);
        console.log(temp);
        return;
    }

    const startBrawl = async (characterId: string, weaponId: string, targetId: string) => {
        console.log(`Initiating Brawl between ${characterId} and target: ${targetId}`);
        // setCanSelectAction(false);
        args.setModalContent(null);
        const request: BrawlRequest = {
            GameId: gameId,
            ActingUserId: args.loggedInUser.userId,
            ActingCharacterId: characterId,
            WeaponId: weaponId,
            Target: { CharacterId: targetId },
            EnabledTraitIds: []
        }
        const temp = await runningConnection.invoke('Brawl', request);
        console.log(temp);
        return;
    }

    const submitGetMoving = async (characterId: string, movementOrderCharacterIds: string[]) => {
        console.log("initialize get moving");
        const request: RequestGetMoving = {
            characterId: characterId,
            movementOrderCharacterIds: movementOrderCharacterIds
        };
        // setCanSelectAction(false);
        args.setModalContent(null);
        const temp = await runningConnection.invoke('GetMoving', request);
        console.log(temp);
        return;
    }

    const submitBackOff = async (characterId: string) => {
        console.log("initialize back off");
        const request: RequestBackOff = {
            characterId: characterId,
        };
        args.setModalContent(null);
        const temp = await runningConnection.invoke('BackOff', request);
        console.log(temp);
        return;
    }

    const submitPatchUp = async (characterId: string) => {
        console.log("initialize patch up");
        const request: RequestPatchUp = {
            characterId: characterId,
        };
        args.setModalContent(null);
        const temp = await runningConnection.invoke('PatchUp', request);
        console.log(temp);
        return;
    }

    const submitDevForcePosition = async (characterId: string, position: Vector3, rotation: Quaternion) => {
        console.log("initialize submitDevForcePosition");
        const request: RequestDevForceSyncPositions = {
            transforms: {
                [characterId]: {
                    position: position,
                    rotation: rotation
                }
            },
        };
        args.setModalContent(null);
        console.log(request);
        const temp = await runningConnection.invoke('DevForceSyncPositions', request);
        console.log(temp);
        return;
    }

    const submitRotateCharacter = async (characterId: string, rotation: Quaternion) => {
        console.log("initialize submitRotateCharacter");
        const request: RotateCharacterRequest = {
            characterId,
            rotation
        };
        args.setModalContent(null);
        console.log(request);
        const temp = await runningConnection.invoke('RotateCharacter', request);
        console.log(temp);
        return;
    }

    if (error) {
        return <div>There was an error connection: {error}</div>
    }
    if (!runningConnection) {
        return <div>Connecting to hub...</div>
    }
    if (isLoading) {
        return <div>Loading Game...</div>
    }

    // const promptDisplay = (prompt !== null) ? <div>User: {prompt.promptedPlayerUserId} prompted to: {GetPromptType(prompt.type)}</div> : null;

    return <LoadedGameView
        game={game}
        phase={phase}
        startOpenFire={startOpenFire}
        startBrawl={startBrawl}
        loggedInUser={args.loggedInUser}
        setModalContent={args.setModalContent}
        prompt={gamePrompt}
        actionPrompt={actionPrompt}
        diceRolls={diceRolls}
        passHandler={passHandler}
        endTurnHandler={endTurnHandler}
        resetHandler={resetHandler}
        round={round}
        submitGetMoving={submitGetMoving}
        submitBackOff={submitBackOff}
        submitPatchUp={submitPatchUp}
        submitDevForcePosition={submitDevForcePosition}
        submitRotateCharacter={submitRotateCharacter}
        scapeConnection={args.scapeConnection}
        narrativeEvents={narrativeEvents}
    />
}


// type GameDataViewArgs = {
//     game: GameData;
//     loggedInUser: LoggedInUser;
// }

// const GameDataView: React.FC<GameDataViewArgs> = (args) => {
//     const game = args.game;
// }


interface MessagesViewArgs {
    runningConnection: signalR.HubConnection;
}

const MessagesView: React.FC<MessagesViewArgs> = (args) => {
    const [messages, setMessages] = useState<string[]>([]);

    // args.runningConnection.on("ScapesUpdated", (message: any) => {
    //     console.log("Global message received:");
    //     console.log(message);
    //     // Update your state or UI here as needed
    // });

    // useEffect(() => {
    // console.log("Sending test call");
    // args.runningConnection.invoke('Connect', {GameId: '88888888-eeda-48c4-a844-f01a2ed504c5' })
    //     .then(res => {
    //         console.log('got test response');
    //         console.log(res);
    //     }).catch(err => {
    //         console.log('error on test call');
    //         console.log(err);
    //     })
    // }, []);

    return (
        <div>
            <h1>📡 SignalR Messages - CONNECTED TO GAME!!</h1>
            <ul>
                {/* {messages.map((msg, idx) => <li key={idx}>{msg}</li>)} */}
            </ul>
            <Link to='two'>Second</Link>
        </div>
    );
}


const getActionTypeString = (type: FalloutActionType): string => {
    switch (type) {
        case "backOff": return "Back Off";
        case "brawl": return "Brawl";
        case "getMoving": return "Get Moving";
        case "openFire": return "Open Fire";
        case "patchUp": return "Patch Up";
    }
}


export default GameView;
